/**
 * @file Checklist and quick-close API helpers shared by parent-board views.
 */

import {
    doc as firestoreDoc,
    serverTimestamp as firestoreServerTimestamp,
    updateDoc as firestoreUpdateDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import {
    toggleChecklistItemCallable as configuredToggleChecklistItemCallable,
    updateChecklistItemsCallable as configuredUpdateChecklistItemsCallable,
} from "../../app/firebase/callables.js";
import { db as configuredDb } from "../../app/firebase/firebase.js";
import {
    appDataDetails as configuredAppDataDetails,
    state as configuredState,
} from "../../app/state/state.js";
import {
    findItem as configuredFindItem,
} from "../utils/itemLookup.js";
import {
    getTaskQuickCloseStatusConfig as configuredGetTaskQuickCloseStatusConfig,
} from "../utils/taskDisplay.js";
import {
    resolveItemClosedFromStatus as configuredResolveItemClosedFromStatus,
} from "../utils/closedState.js";

/**
 * Creates checklist API helpers with Firebase callable dependencies injected by
 * the compatibility API surface.
 * @param {object} deps Dependency bag.
 * @returns {object} Checklist API helpers.
 */
export function createChecklistApi({
    appDataDetails,
    db,
    doc,
    findItem,
    getResolvedClosedUpdate,
    getTaskQuickCloseStatusConfig,
    serverTimestamp,
    state,
    toggleChecklistItemCallable,
    updateChecklistItemsCallable,
    updateDoc,
}) {
    /**
     * Quickly moves a task to the configured quick-close status.
     * @param {string} taskId Task document ID.
     * @returns {Promise<void>}
     */
    const quickCloseTask = async (taskId) => {
        const quickCloseStatus = getTaskQuickCloseStatusConfig();
        if (!taskId || !quickCloseStatus) {return;}

        const task = findItem(taskId) ||
            appDataDetails?.tasks?.find((entry) => entry.id === taskId);
        if (!task || task.status === quickCloseStatus.id) {return;}

        const docRef = doc(db, "tasks", taskId);
        await updateDoc(docRef, {
            status: quickCloseStatus.id,
            isSubtask: Boolean(task.parentId),
            updatedBy: state.currentUser.uid,
            updatedAt: serverTimestamp(),
            ...getResolvedClosedUpdate("task", quickCloseStatus.id),
        });
    };

    /**
     * Toggles the completion status of a checklist item.
     * @param {string} itemId Item document ID.
     * @param {string} itemType Parent item type.
     * @param {string} checklistItemId Checklist entry ID.
     * @param {boolean} isCompleted New completion state.
     * @returns {Promise<boolean>} Success state from the callable.
     */
    const toggleChecklistItem = async (
        itemId,
        itemType,
        checklistItemId,
        isCompleted,
    ) => {
        try {
            const result = await toggleChecklistItemCallable({
                itemId,
                itemType,
                checklistItemId,
                isCompleted,
            });
            return result.data.success;
        } catch (error) {
            console.error('Error toggling checklist item:', error);
            return false;
        }
    };

    /**
     * Saves the current checklist map.
     * @param {string} itemId Item document ID.
     * @param {string} itemType Parent item type.
     * @param {object} checklistMap Checklist map payload.
     * @returns {Promise<boolean>} Success state from the callable.
     */
    const saveChecklistState = async (itemId, itemType, checklistMap) => {
        try {
            const result = await updateChecklistItemsCallable({
                itemId,
                itemType,
                checklist: checklistMap,
            });
            return result.data.success;
        } catch (error) {
            console.error('Error saving checklist state:', error);
            return false;
        }
    };

    /**
     * Handles checklist toggles from compact Kanban card controls.
     * @param {Event} event Click event.
     * @param {string} itemId Item document ID.
     * @param {string} itemType Parent item type.
     * @param {string} checklistItemId Checklist entry ID.
     * @returns {Promise<void>}
     */
    const handleKanbanChecklistToggle = async (
        event,
        itemId,
        itemType,
        checklistItemId,
    ) => {
        event.stopPropagation();
        const btn = event.currentTarget;
        const tickPath = btn.querySelector('.checklist-toggle-tick');
        const textSpan = btn.nextElementSibling;
        btn.disabled = true;
        btn.classList.remove('text-transparent', 'bg-transparent', 'border-gray-400');
        btn.classList.add('bg-gray-400', 'border-gray-400', 'text-white');
        tickPath?.classList.remove('opacity-0');
        tickPath?.classList.add('opacity-100');
        if (textSpan) {
            textSpan.classList.add('line-through', 'text-gray-400', 'opacity-50');
        }
        try {
            await toggleChecklistItem(itemId, itemType, checklistItemId, true);
        } catch (error) {
            console.error("Failed to toggle Kanban checklist item:", error);
            btn.disabled = false;
            btn.classList.remove('bg-gray-400', 'border-gray-400', 'text-white');
            btn.classList.add('text-transparent', 'bg-transparent', 'border-gray-400');
            tickPath?.classList.remove('opacity-100');
            tickPath?.classList.add('opacity-0');
            if (textSpan) {
                textSpan.classList.remove('line-through', 'text-gray-400', 'opacity-50');
            }
        }
    };

    return {
        handleKanbanChecklistToggle,
        quickCloseTask,
        saveChecklistState,
        toggleChecklistItem,
    };
}

const configuredGetResolvedClosedUpdate = (itemType, statusId) => ({
    isClosedResolved: configuredResolveItemClosedFromStatus(itemType, statusId),
});

const configuredChecklistApi = createChecklistApi({
    appDataDetails: configuredAppDataDetails,
    db: configuredDb,
    doc: firestoreDoc,
    findItem: configuredFindItem,
    getResolvedClosedUpdate: configuredGetResolvedClosedUpdate,
    getTaskQuickCloseStatusConfig: configuredGetTaskQuickCloseStatusConfig,
    serverTimestamp: firestoreServerTimestamp,
    state: configuredState,
    toggleChecklistItemCallable: configuredToggleChecklistItemCallable,
    updateChecklistItemsCallable: configuredUpdateChecklistItemsCallable,
    updateDoc: firestoreUpdateDoc,
});

export const handleKanbanChecklistToggle =
    configuredChecklistApi.handleKanbanChecklistToggle;
export const quickCloseTask = configuredChecklistApi.quickCloseTask;
export const saveChecklistState = configuredChecklistApi.saveChecklistState;
export const toggleChecklistItem = configuredChecklistApi.toggleChecklistItem;

window.handleKanbanChecklistToggle = handleKanbanChecklistToggle;
